import { useEffect, useRef, useState } from 'react';
import { Box, Chip, Fade, IconButton, Tooltip, Typography } from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';

// Keys are what the API accepts (customer-chatbot-api src/feedback.ts).
const FEEDBACK_REASONS = [
    { key: 'off_topic', label: '違う回答' },
    { key: 'incorrect', label: '間違っている' },
    { key: 'unclear', label: '分かりにくい' },
    { key: 'unresolved', label: '解決しない' }
];

const THANKS = 'ご回答ありがとうございます';
// Long enough to read, short enough not to clutter the conversation.
const THANKS_SHOWN_MS = 2000;

/**
 * 👍/👎 for a finished answer (PPS-390). Kept small: it sits on the sources
 * row, and the chosen thumb stays highlighted (and can be changed). A short
 * thank-you confirms the reason after 👎 -- the one click whose result
 * (the reasons row closing) could read as nothing having happened.
 */
export const FeedbackThumbs = ({ feedback, onRate, color }) => {
    const rating = feedback?.rating;
    const [thanked, setThanked] = useState(false);
    // Only for a reason picked now -- not when the widget remounts showing an
    // earlier rating. Forgotten when the reason is cleared, so picking it
    // again after switching thumbs thanks again.
    const seen = useRef(feedback?.reason);
    useEffect(() => {
        const reason = feedback?.reason;
        if (!reason) {
            seen.current = undefined;
            return;
        }
        if (reason === seen.current) return;
        seen.current = reason;
        setThanked(true);
    }, [feedback]);
    // Its own effect, so a later click cannot cancel the timer and leave it up.
    useEffect(() => {
        if (!thanked) return undefined;
        const timer = setTimeout(() => setThanked(false), THANKS_SHOWN_MS);
        return () => clearTimeout(timer);
    }, [thanked]);

    const thumb = (value, Outlined, Filled, label) => {
        const chosen = rating === value;
        const Icon = chosen ? Filled : Outlined;
        return (
            <Tooltip title={chosen ? THANKS : label}>
                <IconButton
                    size="small"
                    aria-label={chosen ? `${label}（${THANKS}）` : label}
                    onClick={() => !chosen && onRate(value)}
                    sx={{ color: chosen ? color : 'text.secondary', p: 0.5 }}
                >
                    <Icon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
        );
    };
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {thumb('good', ThumbUpAltOutlinedIcon, ThumbUpAltIcon, '役に立った')}
            {thumb('bad', ThumbDownAltOutlinedIcon, ThumbDownAltIcon, '役に立たなかった')}
            {/* The live region is always there so the text appearing is
                announced; unmounted when hidden so it takes no width. */}
            <Box role="status" sx={{ ml: 0.5 }}>
                <Fade in={thanked} timeout={{ enter: 150, exit: 600 }} unmountOnExit>
                    <Typography variant="caption" color="text.secondary">
                        ありがとうございます
                    </Typography>
                </Fade>
            </Box>
        </Box>
    );
};

/** One row of optional reasons, shown after 👎 until one is picked. */
export const FeedbackReasons = ({ feedback, onRate, color }) => {
    if (feedback?.rating !== 'bad' || feedback.reason) return null;
    return (
        <Box role="group" aria-label="理由" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
                理由：
            </Typography>
            {FEEDBACK_REASONS.map(({ key, label }) => (
                <Chip
                    key={key}
                    size="small"
                    variant="outlined"
                    label={label}
                    onClick={() => onRate('bad', key)}
                    sx={{ borderColor: color, color, height: 22, fontSize: 12 }}
                />
            ))}
        </Box>
    );
};
